import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useCurrentUser } from "@/lib/auth";
import { getAbsenceTypeLabel } from "@/lib/employeeAbsences";
import {
  getMyDay,
  requestAssignmentFeinplanung,
  type PlanungAssignment,
  type PlanungMyDay,
  type PlanungOrderLink,
} from "@/lib/montageplanung";
import { notifyMontageplanungBadgeRefresh } from "@/lib/navBadges";
import { ensureOverviewGroups } from "@/projectOverview/permissions";
import { AssignmentProjectActions } from "@/pages/montageplanung/AssignmentProjectActions";
import { addDays, formatDateDe, parseIso, toIso } from "@/pages/montageplanung/dateUtils";
import { MyDayOrderMontageBlock } from "@/pages/home/MyDayOrderMontageBlock";

function formatWeekday(iso: string): string {
  const parsed = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("de-DE", { weekday: "long" });
}

function todayIso(): string {
  return toIso(new Date());
}

function shiftDateIso(iso: string, days: number): string {
  return toIso(addDays(parseIso(iso), days));
}

function ordersForAssignmentDay(assignment: PlanungAssignment): PlanungOrderLink[] {
  const feinplanungOrders = assignment.feinplanung_day?.orders ?? [];
  if (feinplanungOrders.length > 0) return feinplanungOrders;
  return assignment.orders ?? [];
}

function hasFeinplanungForDay(assignment: PlanungAssignment): boolean {
  const day = assignment.feinplanung_day;
  if (!day) return false;
  return (day.orders?.length ?? 0) > 0 || Boolean(day.note?.trim());
}

function emptyDayMessage(viewDate: string): string {
  return viewDate === todayIso()
    ? "Heute sind keine Einsätze für Sie geplant."
    : "An diesem Tag sind keine Einsätze für Sie geplant.";
}

export function MyDayPanel() {
  const { user } = useCurrentUser();
  const [viewDate, setViewDate] = useState(todayIso);
  const [data, setData] = useState<PlanungMyDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [requestingId, setRequestingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getMyDay(viewDate));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mein Tag konnte nicht geladen werden.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [viewDate]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!user) {
      setGroups([]);
      return;
    }
    void ensureOverviewGroups(user).then(setGroups);
  }, [user]);

  const displayDate = data?.date.slice(0, 10) ?? viewDate;
  const isToday = displayDate === todayIso();
  const assignments = data?.assignments ?? [];
  const absence = data?.absence ?? null;

  const dateHeading = useMemo(() => {
    if (!displayDate) return "Einsätze und Projekte";
    return `${formatWeekday(displayDate)}, ${formatDateDe(displayDate)}`;
  }, [displayDate]);

  const dateNav = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
        aria-label="Vorheriger Tag"
        onClick={() => setViewDate((current) => shiftDateIso(current, -1))}
      >
        ‹
      </button>
      <span className="min-w-0 text-sm font-medium text-label">{dateHeading}</span>
      <button
        type="button"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
        aria-label="Nächster Tag"
        onClick={() => setViewDate((current) => shiftDateIso(current, 1))}
      >
        ›
      </button>
      {!isToday ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-7 px-2.5 text-[11px]"
          onClick={() => setViewDate(todayIso())}
        >
          Heute
        </Button>
      ) : null}
    </div>
  );

  return (
    <Card
      title="Mein Tag"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {dateNav}
          <Link
            to="/montageplanung"
            className="inline-flex items-center rounded-full border border-separator px-3 py-1 text-xs font-medium text-label-secondary transition hover:border-accent hover:text-accent"
          >
            Montageplanung
          </Link>
        </div>
      }
      bodyClassName="space-y-3"
    >
      {loading && !data ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-status-red">{error}</p>
      ) : (
        <>
          {absence && (
            <div className="rounded-ios border border-separator bg-fill-quaternary/50 px-3 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
                Abwesenheit
              </div>
              <p className="mt-1 text-sm font-medium text-label">
                {getAbsenceTypeLabel(absence.absence_type)}
                {absence.status === "pending" ? " (beantragt)" : ""}
              </p>
            </div>
          )}

          {assignments.length === 0 && !absence && (
            <p className="text-sm text-label-secondary">{emptyDayMessage(displayDate)}</p>
          )}

          {assignments.map((assignment) => {
            const dayOrders = ordersForAssignmentDay(assignment);
            const feinplanungNote = assignment.feinplanung_day?.note?.trim() || null;
            const planungNote = assignment.note?.trim() || null;
            const showFeinplanung = hasFeinplanungForDay(assignment);

            return (
              <article
                key={assignment.id}
                className="rounded-ios border border-separator bg-fill-quaternary/30 px-3 py-2.5"
              >
                <div className="flex items-start gap-2">
                  <span
                    className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm"
                    style={{ backgroundColor: assignment.color || "#0a84ff" }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-label">{assignment.title}</h3>
                    {(assignment.date_from.slice(0, 10) !== displayDate ||
                      assignment.date_to.slice(0, 10) !== displayDate) && (
                      <p className="mt-0.5 text-xs text-label-secondary">
                        {formatDateDe(assignment.date_from)} – {formatDateDe(assignment.date_to)}
                      </p>
                    )}
                  </div>
                </div>

                {planungNote ? (
                  <div className="mt-2.5 border-t border-separator pt-2">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
                      Hinweis für diesen Einsatz
                    </div>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
                      {planungNote}
                    </p>
                  </div>
                ) : null}

                {showFeinplanung ? (
                  <div className="mt-2.5 border-t border-separator pt-2">
                    <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
                      Feinplanung
                    </div>

                    {dayOrders.map((order) => {
                      const label = order.order_label?.trim() || `Auftrag ${order.order_id}`;
                      return (
                        <div key={order.order_id} className="mb-2 last:mb-0">
                          <p className="text-sm font-medium text-label">{label}</p>
                          <MyDayOrderMontageBlock
                            orderId={order.order_id}
                            orderLabel={label}
                            user={user}
                            groups={groups}
                          />
                        </div>
                      );
                    })}

                    {feinplanungNote ? (
                      <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
                        {feinplanungNote}
                      </p>
                    ) : null}

                    {dayOrders.length > 0 ? (
                      <div className="mt-2.5 border-t border-separator pt-2">
                        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
                          Projekte
                        </div>
                        <AssignmentProjectActions orders={dayOrders} compact />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {assignment.can_request_feinplanung ? (
                  <div className="mt-2.5 border-t border-separator pt-2">
                    <button
                      type="button"
                      className="text-xs font-medium text-tint hover:underline disabled:opacity-60"
                      disabled={requestingId === assignment.id}
                      onClick={() => {
                        setRequestingId(assignment.id);
                        void requestAssignmentFeinplanung(assignment.id)
                          .then(() => {
                            notifyMontageplanungBadgeRefresh();
                            return load();
                          })
                          .catch((err) => {
                            setError(
                              err instanceof Error
                                ? err.message
                                : "Antrag konnte nicht gestellt werden.",
                            );
                          })
                          .finally(() => setRequestingId(null));
                      }}
                    >
                      {requestingId === assignment.id
                        ? "Antrag wird gestellt…"
                        : "Feinplanung beantragen"}
                    </button>
                  </div>
                ) : null}

                {assignment.feinplanung_request_pending ? (
                  <p className="mt-2 text-xs font-medium text-status-orange">
                    Feinplanung beantragt – wartet auf Freigabe durch das Büro.
                  </p>
                ) : null}
              </article>
            );
          })}
        </>
      )}
    </Card>
  );
}
